import { times } from 'lodash'
import {
  BaseDBService,
  BaseDBServiceOpts,
  Context,
  CoreDict,
  HashingService,
  Id,
  createObj,
  enumGetValues,
  runIfDefined,
  throwInternalError,
  throwInvalidOperation,
  throwObjectNotFound,
  trCore,
} from '../../../common'
import { CreateEventMode, ModelEvent, ModelEventCreator, ModelEventsProcessor } from '../../events'
import { AuthCode, AuthCodeGenerationResult, AuthCodesRepository, AuthCodesService } from './authCodes'
import { Permissions } from './permissions'
import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db'

interface DBAuthCodesServiceOpts extends BaseDBServiceOpts<CoreDict> {
  authCodesRepository: AuthCodesRepository
  hashingService: HashingService
  codeSymbols?: string
  codeLength: number
}

enum EventType {
  AUTH_CODE_GENERATE = 'AUTH_CODE_GENERATE',
  AUTH_CODE_USE = 'AUTH_CODE_USE',
  AUTH_CODE_MERGE = 'AUTH_CODE_MERGE',
}

type GenerateAuthCodePayload = {
  codeHash: string
}
type UseAuthCodePayload = {
  userId: Id
}
type MergeAuthCodePayload = {
  codeHash: string
  createdAt: string
  deletedAt?: string
  issuedByUserId: Id
  usedAt?: string
  usedByUserId?: Id
}

export class DBAuthCodesService extends BaseDBService<DBAuthCodesServiceOpts, CoreDict> implements AuthCodesService {
  constructor(opts: DBAuthCodesServiceOpts) {
    super('DBAuthCodesService', opts)
    this.logger.debug({}, '<fc7a113e> Initialized')
  }

  private generateCode(): string {
    const symbols = this.opts.codeSymbols ?? '0123456789'
    return times(this.opts.codeLength, () => symbols[Math.floor(Math.random() * symbols.length)]).join('')
  }

  async generate(ctx: Context): Promise<AuthCodeGenerationResult> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.AUTH_CODES_GENERATE)

    const visibleCode = this.generateCode()

    const event = await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId: this.opts.idGenerator.generate(),
        type: EventType.AUTH_CODE_GENERATE,
        payload: createObj<GenerateAuthCodePayload>({
          codeHash: this.opts.hashingService.hash(visibleCode),
        }),
      }),
    )
    await this.opts.eventDispatcher.processEvents(ctx, [event])
    return {
      visibleCode,
    }
  }

  async findByCode(ctx: Context, code: string): Promise<AuthCode | undefined> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.AUTH_CODES_USE)
    const codeHash = this.opts.hashingService.hash(code)
    return await runWithROTransaction(this.opts.dbConnection, tx =>
      this.opts.authCodesRepository.findByCodeHash(tx, codeHash),
    )
  }

  async useByUser(ctx: Context, authCodeId: Id, userId: Id): Promise<void> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.AUTH_CODES_USE)
    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      const authCode =
        (await this.opts.authCodesRepository.findById(tx, authCodeId)) ??
        throwObjectNotFound(`<99250b79> ${this.translate(ctx, d => d.authCodes.notFoundByIdError, authCodeId)}`)

      if (authCode.usedByUserId !== undefined && authCode.usedByUserId !== userId) {
        throwInvalidOperation(`<3b375ad5> ${this.translate(ctx, d => d.authCodes.alreadyUsedByAnotherUserError)}`)
      }

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: authCodeId,
        type: EventType.AUTH_CODE_USE,
        payload: createObj<UseAuthCodePayload>({
          userId,
        }),
      })
    })
    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }
}

export class AuthCodesModelEventsProcessor implements ModelEventsProcessor {
  supportedEventTypes = enumGetValues(EventType)

  constructor(private opts: { authCodesRepository: AuthCodesRepository; eventCreator: ModelEventCreator }) {}

  // UPDATED
  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const c =
      (await this.opts.authCodesRepository.findById(tx, entityId)) ??
      throwObjectNotFound(
        trCore(d => d.authCodes.notFoundByIdError, entityId),
        { uid: '<ec59aa14>' },
      )
    return this.opts.eventCreator.create(ctx, tx, {
      entityId,
      mode: CreateEventMode.UPDATE,
      type: EventType.AUTH_CODE_MERGE,
      payload: createObj<MergeAuthCodePayload>({
        codeHash: c.codeHash,
        issuedByUserId: c.issuedByUserId,
        usedByUserId: c.usedByUserId,
        createdAt: c.createdAt.toISOString(),
        deletedAt: c.deletedAt?.toISOString(),
        usedAt: c.usedAt?.toISOString(),
      }),
    })
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type as EventType) {
      case EventType.AUTH_CODE_GENERATE: {
        const p = event.payload as GenerateAuthCodePayload
        await this.opts.authCodesRepository.save(tx, {
          id: event.entityId,
          createdAt: event.createdAt,
          issuedByUserId: event.userId ?? throwInternalError('<5a7dcaeb> No issuer'),
          codeHash: p.codeHash,
        })
        return true
      }
      case EventType.AUTH_CODE_USE: {
        const p = event.payload as UseAuthCodePayload
        const authCode =
          (await this.opts.authCodesRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(
            trCore(d => d.authCodes.notFoundByIdError, event.entityId),
            { uid: '<f41a0019>' },
          )
        await this.opts.authCodesRepository.save(tx, {
          ...authCode,
          usedAt: event.createdAt,
          usedByUserId: p.userId,
        })
        return true
      }
      case EventType.AUTH_CODE_MERGE: {
        const p = event.payload as MergeAuthCodePayload
        await this.opts.authCodesRepository.save(tx, {
          id: event.entityId,
          codeHash: p.codeHash,
          createdAt: new Date(p.createdAt),
          deletedAt: runIfDefined(p.deletedAt, v => new Date(v)),
          issuedByUserId: p.issuedByUserId,
          usedAt: runIfDefined(p.usedAt, v => new Date(v)),
          usedByUserId: p.usedByUserId,
        })
        return true
      }
    }
    return false
  }
}
